import ChoicePicker from '../components/ChoicePicker';
import ContinueButtonBar from '../components/ContinueButtonBar';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import { WORK_HISTORY_CORRECTION_CATEGORIES } from './healthInfoZuidoSteps';
import type { useHealthInfoZuidoFlow } from './useHealthInfoZuidoFlow';
import type { WorkHistoryCorrectionCategory, YesNo } from './healthInfoZuidoTypes';
import './HealthInfoZuidoIntroComposer.css';

type Flow = ReturnType<typeof useHealthInfoZuidoFlow>;

const FORM_AGREE_OPTIONS = (t: Flow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.healthInfoZuido.iAgree') },
  { value: 'no' as const, label: t('submitDocuments.healthInfoZuido.iDoNotAgree') },
];

const WORK_HISTORY_CONFIRM_OPTIONS = (t: Flow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.healthInfoZuido.workHistory.yesCorrect') },
  { value: 'no' as const, label: t('submitDocuments.healthInfoZuido.workHistory.noError') },
];

const WORK_HISTORY_CORRECTION_OPTIONS = (t: Flow['t']) =>
  WORK_HISTORY_CORRECTION_CATEGORIES.map((value) => ({
    value,
    label: t(`submitDocuments.healthInfoZuido.workHistory.correctionCategories.${value}`),
  }));

export interface HealthInfoZuidoChatComposerProps {
  flow: Flow;
  onOpenConsentPdfRead?: () => Promise<void>;
  onOpenWorkHistoryFormPdf?: () => Promise<void>;
}

export default function HealthInfoZuidoChatComposer({
  flow,
  onOpenConsentPdfRead,
  onOpenWorkHistoryFormPdf,
}: HealthInfoZuidoChatComposerProps) {
  const {
    currentStep,
    t,
    advanceIntro,
    advanceFormAgree,
    advanceWorkHistoryIntro,
    advanceConfirmWorkHistory,
    advanceWorkHistoryCorrection,
    markConsentRead,
    markWorkHistoryFormViewed,
    values,
  } = flow;
  if (!currentStep) return null;

  if (currentStep.stepId === 'intro') {
    return (
      <div className="health-info-zuido-intro-composer">
        <PdfPreviewActionButton
          label={t('submitDocuments.healthInfoZuido.readConsentForm')}
          onClick={() => {
            void (async () => {
              if (onOpenConsentPdfRead) {
                await onOpenConsentPdfRead();
              }
              await markConsentRead();
            })();
          }}
        />
        <ContinueButtonBar
          label={t('submitDocuments.chat.continue')}
          onContinue={() => void advanceIntro()}
        />
      </div>
    );
  }

  if (currentStep.stepId === 'workHistoryIntro') {
    return (
      <div className="health-info-zuido-intro-composer">
        <PdfPreviewActionButton
          label={t('submitDocuments.healthInfoZuido.workHistory.viewCompletedForm')}
          onClick={() => {
            void (async () => {
              if (onOpenWorkHistoryFormPdf) {
                await onOpenWorkHistoryFormPdf();
              }
              await markWorkHistoryFormViewed();
            })();
          }}
        />
        <ContinueButtonBar
          label={t('submitDocuments.chat.continue')}
          onContinue={() => void advanceWorkHistoryIntro()}
        />
      </div>
    );
  }

  if (currentStep.stepId === 'agreeForm31' || currentStep.stepId === 'agreeForm32') {
    const field = currentStep.stepId === 'agreeForm31' ? 'agreeForm31' : 'agreeForm32';
    const raw = values[field];
    const current = raw === 'yes' || raw === 'no' ? raw : null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={current}
          options={FORM_AGREE_OPTIONS(t)}
          onChange={(v) => void advanceFormAgree(currentStep.stepId, v as YesNo)}
        />
      </div>
    );
  }

  if (currentStep.stepId === 'confirmWorkHistory') {
    const raw = values.workHistoryInfoCorrect;
    const current = raw === 'yes' || raw === 'no' ? raw : null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={current}
          options={WORK_HISTORY_CONFIRM_OPTIONS(t)}
          onChange={(v) => void advanceConfirmWorkHistory(v as YesNo)}
        />
      </div>
    );
  }

  if (currentStep.stepId === 'workHistoryCorrection') {
    const raw = values.workHistoryCorrectionCategory;
    const current =
      raw && WORK_HISTORY_CORRECTION_CATEGORIES.includes(raw as WorkHistoryCorrectionCategory)
        ? (raw as WorkHistoryCorrectionCategory)
        : null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={current}
          options={WORK_HISTORY_CORRECTION_OPTIONS(t)}
          onChange={(v) => void advanceWorkHistoryCorrection(v as WorkHistoryCorrectionCategory)}
        />
      </div>
    );
  }

  return null;
}

export function HealthInfoZuidoDeclinedComposer({ onContinue }: { onContinue: () => void }) {
  return <ContinueButtonBar onContinue={onContinue} />;
}
